import { dataApi } from "./dataApi";
import { groupApi } from "./groupApi";
import { schemaApi } from "./schemaApi";
import type { CreateGroupInput } from "./groupApi";
import type { CreateSchemaInput } from "./schemaApi";
import type {
  CatalogSourceCandidate,
  DataSourceRef,
} from "@/types/componentDataBinding";

interface PmSourceIdentity {
  dataOwnerOrganizationId: string;
  groupId: string;
  schemaId: string;
}

export interface PmDatasetSourceTemplate {
  schema: CreateSchemaInput;
  group: CreateGroupInput;
}

export interface PmDatasetCreateResult {
  source: DataSourceRef;
  candidate: CatalogSourceCandidate;
}

export interface PmDatasetSampleRecord {
  data: Record<string, unknown>;
  tags?: string[];
}

function buildSourceSuffix(): string {
  return Date.now().toString(36);
}

export function buildEmptyPmDatasetSourceTemplate(): PmDatasetSourceTemplate {
  const suffix = buildSourceSuffix();

  return {
    schema: {
      name: `pm_items_${suffix}`,
      displayName: "PM Items",
      description: "Empty PM dataset for project management components.",
      fields: [
        {
          id: "field_title",
          name: "title",
          type: "string",
          required: true,
        },
        {
          id: "field_status",
          name: "status",
          type: "string",
          required: false,
        },
        {
          id: "field_assignee",
          name: "assignee",
          type: "string",
          required: false,
        },
        {
          id: "field_due_date",
          name: "dueDate",
          type: "date",
          required: false,
        },
      ],
      visibility: "private",
      tags: ["pm"],
    },
    group: {
      name: "PM Items",
      slug: `pm-items-${suffix}`,
      description: "Empty PM source for project management components.",
    },
  };
}

function requireNonEmptyString(value: unknown, fieldName: string): string {
  if (typeof value !== "string" || value.trim().length === 0) {
    throw new Error(`${fieldName} is required to create a PM dataset source.`);
  }

  return value.trim();
}

function getPmSourceIdentity(params: {
  requestingOrganizationId: string;
  schemaOrganizationId: string;
  groupOrganizationId: string;
  membershipOrganizationId: string;
  membershipGroupId: string;
  membershipSchemaId: string;
}): PmSourceIdentity {
  const requestingOrganizationId = requireNonEmptyString(
    params.requestingOrganizationId,
    "requestingOrganizationId",
  );
  const schemaOrganizationId = requireNonEmptyString(
    params.schemaOrganizationId,
    "schema.organizationId",
  );
  const groupOrganizationId = requireNonEmptyString(
    params.groupOrganizationId,
    "group.organizationId",
  );
  const membershipOrganizationId = requireNonEmptyString(
    params.membershipOrganizationId,
    "membership.organizationId",
  );

  if (
    schemaOrganizationId !== groupOrganizationId ||
    schemaOrganizationId !== membershipOrganizationId
  ) {
    throw new Error(
      "Created PM dataset source returned mismatched owner organization identity.",
    );
  }

  if (schemaOrganizationId !== requestingOrganizationId) {
    throw new Error(
      "Created PM dataset source did not match the explicit requesting organization.",
    );
  }

  return {
    dataOwnerOrganizationId: membershipOrganizationId,
    groupId: requireNonEmptyString(params.membershipGroupId, "membership.groupId"),
    schemaId: requireNonEmptyString(params.membershipSchemaId, "membership.schemaId"),
  };
}

export async function createPmDatasetSource(
  template: PmDatasetSourceTemplate,
  requestingOrganizationId: string,
): Promise<PmDatasetCreateResult> {
  const schema = await schemaApi.createSchema(template.schema);
  const group = await groupApi.createGroup(template.group);
  const membership = await groupApi.addSchemaToGroup(group.id, {
    schemaId: schema.id,
  });

  const identity = getPmSourceIdentity({
    requestingOrganizationId,
    schemaOrganizationId: schema.organizationId,
    groupOrganizationId: group.organizationId,
    membershipOrganizationId: membership.organizationId,
    membershipGroupId: membership.groupId,
    membershipSchemaId: membership.schemaId,
  });

  const source: DataSourceRef = {
    kind: "schema",
    dataOwnerOrganizationId: identity.dataOwnerOrganizationId,
    ownerType: "organization",
    ownerId: identity.dataOwnerOrganizationId,
    groupId: identity.groupId,
    schemaId: identity.schemaId,
  };

  const candidate: CatalogSourceCandidate = {
    sourceKind: "schema",
    authorizationPath: "data-service:schema-group-membership",
    source,
    labels: {
      groupName: group.name,
      groupSlug: group.slug,
      schemaName: schema.name,
      schemaDisplayName: schema.displayName,
    },
    capabilities: {
      readable: true,
      writable: true,
    },
  };

  return { source, candidate };
}

export async function createPmDatasetSampleRecords(
  source: DataSourceRef,
  records: PmDatasetSampleRecord[],
): Promise<Array<{ insertedId: string; schemaVersion: number }>> {
  if (records.length === 0) {
    throw new Error("Sample record opt-in requires at least one record.");
  }

  return Promise.all(
    records.map((record) =>
      dataApi.createRecord(
        source.groupId,
        source.schemaId,
        record.data,
        {
          source: "pm-template-sample",
          tags: record.tags ?? ["pm-template-sample"],
        },
        {
          dataOwnerOrganizationId: source.dataOwnerOrganizationId,
        },
      ),
    ),
  );
}
